import fp from 'lodash/fp';
import React, { ComponentType, CSSProperties, ReactChildren, useState } from 'react';
import { FieldRenderProps, FieldMetaState } from 'react-final-form';
import { forms } from '@factorin/container.macro';
import { checkINN, checkSnils, checkBik } from 'ru-validation-codes';
import isKpp from 'is-kpp-js';
import { differenceInCalendarDays, differenceInMinutes } from 'date-fns';
import { castDate, formatDate, formatDateTime } from '@factorin/util/lib';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import { cn, withClassName, Forbid, ClassName } from 'Modules/ui';
import { FormControlBase } from 'Modules/ui2/components';

import { ReactComponent as ShowIcon } from './show.svg';
import styles from './index.module.scss';


type FieldProps = {
    style?: CSSProperties;
    classNames?: {
        container?: string;
        wrapper?: string;
        error?: string;
        label?: string;
        isFocused?: string;
        isValid?: string;
        hasError?: string;
        pristine?: string;
        isRequired?: string;
    };
};

type FormFieldProps = {
    label?: React.ReactNode;
    field?: FieldProps;
};

type AllFormFieldProps = FieldRenderProps<any, HTMLElement> & FormFieldProps;

type ComponentProps = ClassName & {
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLElement | any>) => void;
    onChange?: (event: React.ChangeEvent | any) => void;
    onFocus?: (event: React.FocusEvent<HTMLElement | any>) => void;
    value?: any;
}

export const withFormField = <PComponent extends ComponentProps>(
    Component: ComponentType<Forbid<PComponent, FormFieldProps>>,
): ComponentType<AllFormFieldProps & PComponent> =>
    class extends React.PureComponent<AllFormFieldProps & PComponent> {
        state = {
            focused: false,
        };

        onBlur = (e: React.FocusEvent<HTMLElement>) => this.setState({
            focused: false,
        }, () => this.props.input.onBlur(e));

        onFocus = (e: React.FocusEvent<HTMLElement>) => this.setState({
            focused: true,
        }, () => this.props.input.onFocus(e));

        render() {
            const {
                field: { classNames = {}, style } = {} as FieldProps,
                meta: { touched, error, pristine } = {} as FieldMetaState<any>,
                input, label, touchTriggerDisable, ...props
            } = this.props;

            const componentProps = {
                ...props,
                ...input,
                onFocus: this.onFocus,
                onBlur: this.onBlur,
            } as PComponent;

            const errorAdditionalCondition = touchTriggerDisable || touched && !pristine;

            return (
                <div
                    style={style}
                    className={cn(
                        classNames.formField || styles.formField,
                        classNames.container,
                        this.state.focused && classNames.isFocused,
                        touched && !error && classNames.isValid,
                        touched && error && !pristine && classNames.hasError,
                        pristine && classNames.pristine,
                        error && error.key === 'required' && classNames.isRequired,
                    )}
                >
                    {label && (
                        <label className={cn(styles.label, classNames.label)}>
                            {label}
                        </label>
                    )}
                    <div className={cn(styles.wrapper, classNames.wrapper)}>
                        <Component {...componentProps} />
                    </div>
                    {error && errorAdditionalCondition && (
                        <span className={cn(styles.error, classNames.error)}>
                            {fp.isString(error) ? error : <forms.errorRenderer error={error} />}
                        </span>
                    )}
                </div>
            );
        }
    };

export const TextAreaField = withFormField<JSX.IntrinsicElements['textarea']>(
    withClassName(styles.textarea, 'textarea'),
);

interface InputWithIcon {
    icon?: ReactChildren;
}

export type InputFieldProps = JSX.IntrinsicElements['input'] & InputWithIcon;

export const InputField = withFormField<InputFieldProps>(
    withClassName(styles.input, ({ icon: Icon, ...props }) => Icon
        ? (
            <div className={styles.withIcon}>
                <input {...props} />
                {Icon}
            </div>
        ) : <input {...props} />),
);

export const PasswordField = withFormField<JSX.IntrinsicElements['input']>(
    withClassName(styles.input, props => {
        const [show, setShow] = useState(false);
        return (
            <div className={styles.withIcon}>
                <input {...props} type={show ? 'text' : 'password'} />
                <ShowIcon
                    className={cn(styles.icon, styles.password)}
                    onClick={() => setShow(!show)}
                />
            </div>
        );
    }),
);

export type ErrorDefinition = {
    key: string;
    values?: {
        [key: string]: any;
    };
};

export type Validator<TValue = any> = (value: TValue) => ErrorDefinition | undefined;

export const getError = <TValue extends any>(
    error: string | ErrorDefinition,
    checker: (value: TValue) => boolean,
): Validator<TValue> => fp.pipe(
    checker,
    (valid: boolean) => valid
        ? undefined
        : fp.isString(error)
            ? { key: error }
            : error,
);

const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const emailsRegex = /^([a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*,?)+$/;

const loginRegex = /^(([a-zA-Z0-9.+_-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*)|([a-zA-Z0-9.+_-]{5,50}))$/;

const fioChecker = fp.pipe(
    fp.defaultTo(''),
    fp.split(' '),
    fp.filter(fp.identity),
    fp.get('length'),
    count => count > 1,
);

type NumberFormatter = (value: number) => string;

const defaultValidators = {
    pipe: (...args: Validator[]): Validator => (value: any) => fp.pipe(
        fp.map((arg: Validator) => arg(value)),
        fp.filter(fp.identity),
        fp.first,
    )(args),

    required: getError('required', value => !!value || fp.isNumber(value)),
    requiredKey: (errorKey: string) => getError(errorKey, value => !!value || fp.isNumber(value)),
    requiredArray: getError('required', (value: Array<unknown>) => !!value || Array.isArray(value) ? value.length > 0 : false),

    is: {
        inn: getError<string>({ key: 'inn' }, value => !value || checkINN(value)),

        snils: getError({ key: 'snils' }, checkSnils),

        kpp: getError<string>({ key: 'kpp' }, value => !value || isKpp(value)),

        bik: getError<string>({ key: 'bik' }, value => !value || checkBik(value)),

        login: getError<string>('login', value => loginRegex.test(value)),

        email: getError<string>('email', value => emailRegex.test(value)),

        emails: getError<string>('email', value => emailsRegex.test(value)),

        surnameNameMiddlename: getError<string>(
            'surnameNameMiddlename',
            fioChecker,
        ),

        contactFIO: getError<string>(
            'fioMinTwoPart',
            fioChecker,
        ),

        integer: getError<string>('integer', value => !value || fp.isInteger(+value)),

        finite: getError<string>('finite', value => !value || /^[0-9]*(,|\.|)[0-9]*$/gm.test(value)),

        phone: (mask: string) => getError<string>(
            { key: 'phone' },
            value => value && value.length === mask.length,
        ),

    },

    eq: <TValue extends any>(sample: TValue): Validator<TValue> =>
        getError({ key: 'sample' }, fp.equals(sample)),

    has: {
        nospace: getError<string>({ key: 'nospace' }, value => !(/\s/).test(value)),
        upperCase: getError<string>('upperCase', value => /[A-ZА-Я]/.test(value)),
        digital: getError<string>('digital', value => /\d/.test(value)),
        specSymbols: getError<string>('specSymbols', value => /[?!@%#]/.test(value)),
        notSpecSymbols: getError<string>('noSpecSymbols', value => !/[?!@%#]/.test(value)),
    },

    number: {
        min: (min: number, formatter?: NumberFormatter) => getError<string>(
            { key: 'nummin', values: { min: formatter ? formatter(min) : min } },
            value => {
                if (min && (value || typeof value === 'number' && !Number.isNaN(value)))
                    return fp.lte(min, value);
                return true;
            },
        ),

        max: (max: number, formatter?: NumberFormatter) => getError<string>(
            { key: 'nummax', values: { max: formatter ? formatter(max) : max } },
            value => {
                if (max && (value || typeof value === 'number' && !Number.isNaN(value)))
                    return fp.gte(max, value);
                return true;
            },
        ),
    },
    date: {
        min: (
            min: Date,
            equal?: boolean,
            timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
        ) => getError<Date>(
            { key: equal ? 'dateminEqual' : 'datemin', values: { min: formatDate(utcToZonedTime(min, timeZone)) } },
            (value: Date) => {
                const diff = differenceInCalendarDays(
                    utcToZonedTime(value, timeZone),
                    utcToZonedTime(min, timeZone),
                );
                if (min && value && Number.isFinite(diff))
                    return equal ? diff >= 0 : diff > 0;
                return true;
            },
        ),
        max: (
            max: Date,
            equal?: boolean,
            timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
        ) => getError<Date>(
            { key: equal ? 'datemaxEqual' : 'datemax', values: { max: formatDate(utcToZonedTime(max, timeZone)) } },
            (value: Date) => {
                const diff = differenceInCalendarDays(
                    utcToZonedTime(castDate(value), timeZone),
                    utcToZonedTime(castDate(max), timeZone),
                );
                if (max && value && Number.isFinite(diff))
                    return equal ? diff <= 0 : diff < 0;
                return true;
            },
        ),
    },

    dateTime: {
        min: (
            min: Date,
            equal?: boolean,
            timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
        ) => getError<Date>(
            { key: equal ? 'dateminEqual' : 'datemin', values: { min: formatDateTime(utcToZonedTime(min, timeZone)) } },
            (value: Date) => {
                const diff = differenceInMinutes(
                    zonedTimeToUtc(castDate(value), timeZone),
                    zonedTimeToUtc(castDate(min), timeZone),
                );
                if (min && value && Number.isFinite(diff))
                    return equal ? diff >= 0 : diff > 0;
                return true;
            },
        ),
        max: (
            max: Date,
            equal?: boolean,
            timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
        ) => getError<Date>(
            { key: equal ? 'datemaxEqual' : 'datemax', values: { max: formatDateTime(utcToZonedTime(max, timeZone)) } },
            (value: Date) => {
                const diff = differenceInMinutes(
                    zonedTimeToUtc(castDate(value), timeZone),
                    zonedTimeToUtc(castDate(max), timeZone),
                );
                if (max && value && Number.isFinite(diff))
                    return equal ? diff <= 0 : diff < 0;
                return true;
            },
        ),
    },

    time: {
        min: (
            min: string,
            equal?: boolean,
            timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
        ) => getError<Date>(
            { key: equal ? 'timeMinEqual' : 'timeMin', values: { min } },
            (value: Date) => {
                if (value) {
                    const zonedDate = utcToZonedTime(value, timeZone);
                    const [hh, mm] = min.split(':');
                    const hhValue = zonedDate.getHours();
                    const mmValue = zonedDate.getMinutes();
                    if (Number.isFinite(+hh) &&
                        Number.isFinite(+mm) &&
                        Number.isFinite(hhValue) &&
                        Number.isFinite(mmValue)) {
                        if ((equal ? hhValue >= +hh : hhValue > +hh) ||
                            hhValue === +hh && (equal ? mmValue >= +mm : mmValue > +mm))
                            return true;
                        return false;
                    }
                }
                return true;
            },
        ),
        max: (
            max: string,
            equal?: boolean,
            timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
        ) => getError<Date>(
            { key: equal ? 'timeMaxEqual' : 'timeMax', values: { max } },
            (value: Date) => {
                if (value) {
                    const zonedDate = utcToZonedTime(value, timeZone);
                    const [hh, mm] = max.split(':');
                    const hhValue = zonedDate.getHours();
                    const mmValue = zonedDate.getMinutes();

                    if (Number.isFinite(+hh) &&
                        Number.isFinite(+mm) &&
                        Number.isFinite(hhValue) &&
                        Number.isFinite(mmValue)) {
                        if (hhValue < +hh ||
                            hhValue === +hh && (equal ? mmValue <= +mm : mmValue < +mm))
                            return true;
                        return false;
                    }
                }
                return true;
            },
        ),
    },

    length: {
        min: (min: number) => getError<string>(
            { key: 'min', values: { min } },
            fp.pipe(fp.size, fp.lte(min)),
        ),

        max: (max: number) => getError<string>(
            { key: 'max', values: { max } },
            fp.pipe(fp.size, fp.gte(max)),
        ),
        equal: (equal: number, key: string = 'equal') => getError<string>(
            { key, values: { length: equal } },
            fp.pipe(fp.size, fp.eq(equal)),
        ),
    },
};

export const validators = {
    ...defaultValidators,
    password: defaultValidators.pipe(
        defaultValidators.length.min(8),
        defaultValidators.length.max(100),
        defaultValidators.has.specSymbols,
        defaultValidators.has.digital,
        defaultValidators.has.upperCase,
    ),
};

export const wrapFormComponent = <PComponent extends FormControlBase<any>>(
    Component: ComponentType<PComponent>,
): ComponentType<FieldRenderProps<any, HTMLElement> & PComponent> =>
    ({ input, meta: { error, touched, active }, needInitialValidate, ...props }) => {
        const finalProps = {
            ...input,
            ...props,
            active,
            error: (touched || needInitialValidate) && error
                ? <forms.errorRenderer error={error} />
                : null,
        } as PComponent;
        return (
            <Component {...finalProps} />
        );
    };
